import { fetchImageDataUrls, parseInline, parseRichText, styleName } from "./richText";

// A4 in millimetres, with a comfortable margin.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PT_TO_MM = 0.3528;
const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString();
};

// Load the logo so jsPDF can add it and we know its aspect ratio.
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Build and download a PDF of a guideline's details. `g` is a guideline object
 * (fields: title, category, version, status, last_review_date, author_name,
 * reviewer_name, introduction, sections, references).
 */
export async function exportGuidelinePdf(g) {
  // Lazy-load jsPDF so it stays code-split out of the main bundle.
  const { jsPDF } = await import("jspdf");

  // Flatten the guideline into a single ordered list of render blocks.
  const model = [];
  if (g.category) model.push({ type: "eyebrow", text: g.category });
  model.push({ type: "title", text: g.title || "Untitled guideline" });

  const metaParts = [
    g.author_name && `Author: ${g.author_name}`,
    g.reviewer_name && `Reviewer: ${g.reviewer_name}`,
    g.last_review_date && `Last reviewed: ${fmtDate(g.last_review_date)}`,
    g.version && `Version ${g.version}`,
    g.status && `Status: ${g.status}`,
  ].filter(Boolean);
  if (metaParts.length) model.push({ type: "meta", text: metaParts.join("     ") });
  model.push({ type: "rule" });

  parseRichText(g.introduction).forEach(b => model.push(b));
  (g.sections || []).forEach((s, i) => {
    model.push({ type: "section", text: s.title || `Section ${i + 1}` });
    parseRichText(s.content).forEach(b => model.push(b));
  });

  const references = g.references || [];
  if (references.length) {
    model.push({ type: "rule" });
    model.push({ type: "section", text: "References" });
    references.forEach(r => {
      const text = r.url
        ? (r.title ? `${r.title} — ${r.url}` : r.url)
        : (r.title || "");
      if (text) model.push({ type: "li", prefix: "•  ", text });
    });
  }

  // Pre-fetch every image so the render pass can stay synchronous. One that fails
  // is simply absent, and renders as a placeholder.
  const imageData = await fetchImageDataUrls(
    model.filter(b => b.type === "image").map(b => b.url));

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const ensure = (h) => {
    if (y + h > PAGE_H - MARGIN) { doc.addPage(); y = MARGIN; }
  };

  // Plain single-style paragraph (used for title / headings / meta).
  const writeText = (text, { size = 10, style = "normal", gap = 2, indent = 0, color = [40, 40, 40] } = {}) => {
    if (!text) return;
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lh = size * PT_TO_MM * 1.2;
    const wrapped = doc.splitTextToSize(text, CONTENT_W - indent);
    for (const ln of wrapped) {
      ensure(lh);
      y += lh;
      doc.text(ln, MARGIN + indent, y);
    }
    y += gap;
  };

  // Rich paragraph: lays out styled runs with word-wrap, drawing bold/italic
  // via the font and underline as a drawn line.
  const writeRuns = (runs, { size = 10, gap = 2, indent = 0, color = [40, 40, 40] } = {}) => {
    if (!runs.length) return;
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lh = size * PT_TO_MM * 1.2;
    const x0 = MARGIN + indent;
    const maxW = CONTENT_W - indent;
    let x = x0;
    const startLine = () => { ensure(lh); y += lh; x = x0; };
    startLine();

    const underline = (from, w) => {
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(0.35);
      doc.line(from, y + 0.7, from + w, y + 0.7);
    };
    const placeWord = (word, r) => {
      doc.setFont("helvetica", styleName(r));
      const w = doc.getTextWidth(word);
      if (w > maxW) {                              // token longer than a line (e.g. a URL)
        const parts = doc.splitTextToSize(word, maxW);
        parts.forEach((part, k) => {
          if (x > x0) startLine();
          doc.setFont("helvetica", styleName(r));
          const pw = doc.getTextWidth(part);
          doc.text(part, x, y);
          if (r.underline) underline(x, pw);
          x += pw;
          if (k < parts.length - 1) startLine();
        });
        return;
      }
      if (x + w > x0 + maxW && x > x0) startLine();
      doc.text(word, x, y);
      if (r.underline) underline(x, w);
      x += w;
    };

    for (const r of runs) {
      const tokens = r.text.match(/\S+|\s+/g) || [];
      for (const tok of tokens) {
        if (/^\s+$/.test(tok)) {
          if (x === x0) continue;                  // collapse leading space after a wrap
          doc.setFont("helvetica", styleName(r));
          const sw = doc.getTextWidth(tok);
          if (x + sw > x0 + maxW) {
            startLine();
          } else {
            // Keep the underline continuous across spaces within an underlined run.
            if (r.underline) underline(x, sw);
            x += sw;
          }
        } else {
          placeWord(tok, r);
        }
      }
    }
    doc.setLineWidth(0.2);
    y += gap;
  };

  const drawImage = (url) => {
    const data = imageData[url];
    if (!data) { writeText("[image unavailable]", { size: 9, style: "italic", color: [150, 60, 60] }); return; }
    try {
      const props = doc.getImageProperties(data);
      const naturalWmm = (props.width * 25.4) / 96;
      let w = Math.min(CONTENT_W, naturalWmm);
      let h = (w * props.height) / props.width;
      const maxH = PAGE_H - MARGIN * 2;
      if (h > maxH) { h = maxH; w = (h * props.width) / props.height; }
      ensure(h + 2);
      doc.addImage(data, props.fileType, MARGIN, y, w, h);
      y += h + 3;
    } catch {
      writeText("[image could not be rendered]", { size: 9, style: "italic", color: [150, 60, 60] });
    }
  };

  // Logo at the very top (preserving aspect ratio), like the Incident export.
  try {
    const img = await loadImage(LOGO_URL);
    const logoW = 34;
    const logoH = img.width ? (logoW * img.height) / img.width : 12;
    doc.addImage(img, "PNG", MARGIN, y, logoW, logoH);
    y += logoH + 4;
  } catch {
    /* no logo available — carry on without it */
  }

  for (const b of model) {
    switch (b.type) {
      case "eyebrow": writeText(b.text.toUpperCase(), { size: 9, style: "bold", gap: 0.5, color: [120, 120, 120] }); break;
      case "title": writeText(b.text, { size: 20, style: "bold", gap: 2, color: [20, 20, 20] }); break;
      case "meta": writeText(b.text, { size: 9, gap: 3, color: [110, 110, 110] }); break;
      case "section": writeText(b.text, { size: 13, style: "bold", gap: 1.5, color: [20, 20, 20] }); break;
      case "subheading": writeRuns(parseInline(b.text), { size: 11, gap: 1, color: [40, 40, 40] }); break;
      case "para": writeRuns(parseInline(b.text), { size: 10, gap: 2 }); break;
      case "li":
        writeRuns([{ text: b.prefix, bold: false, italic: false, underline: false }, ...parseInline(b.text)],
                  { size: 10, gap: 0.8, indent: 4 });
        break;
      case "image": drawImage(b.url); break;
      case "rule":
        ensure(4);
        y += 1.5;
        doc.setDrawColor(210, 210, 210);
        doc.setLineWidth(0.2);
        doc.line(MARGIN, y, PAGE_W - MARGIN, y);
        y += 3;
        break;
      default: break;
    }
  }

  // Footer on every page: "© Shelter Stone <year>", centred at the bottom.
  const year = new Date().getFullYear();
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`© Shelter Stone ${year}`, PAGE_W / 2, PAGE_H - 8, { align: "center" });
  }

  const safeName = (g.title || "guideline").replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "") || "guideline";
  doc.save(`${safeName}.pdf`);
}
